import {
	createFileRoute,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import {
	fetchQuery,
	graphql,
	useLazyLoadQuery,
	useMutation,
	useRelayEnvironment,
} from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { Heading } from "@/components/heading";
import { OfferForm, type OfferFormValues } from "@/forms/offer-form";
import { PaymentProviderInfo } from "@/utils/payment-provider-info";
import type { integracoesCheckoutProdutoNovo_offersRefetchQuery } from "./__generated__/integracoesCheckoutProdutoNovo_offersRefetchQuery.graphql";
import type { integracoesCheckoutProdutoNovoMutation } from "./__generated__/integracoesCheckoutProdutoNovoMutation.graphql";
import type { integracoesCheckoutProdutoNovoQuery } from "./__generated__/integracoesCheckoutProdutoNovoQuery.graphql";

const OffersRefetchQuery = graphql`
	query integracoesCheckoutProdutoNovo_offersRefetchQuery($slug: String!) {
		club(slug: $slug) {
			...ofertas_table @arguments(first: 15)
		}
	}
`;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/integracoes/checkout/produto/novo",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			integration: z.string().optional(),
		}),
	),
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const navigate = useNavigate();
	const { integration: lockedIntegrationId } = useSearch({ from: Route.id });
	const environment = useRelayEnvironment();

	const data = useLazyLoadQuery<integracoesCheckoutProdutoNovoQuery>(
		graphql`
		query integracoesCheckoutProdutoNovoQuery($slug: String!) {
			club(slug: $slug) {
				integrations {
					... on PaymentIntegration {
						id
						provider
					}
					__typename
				}

				spaces {
					id
					name
					icon
					group {
						name
					}
				}

				courses(first: 1000) {
					edges {
						node {
							id
							name
							category {
								id
								name
							}
							memberGroups {
								id
								name
								isDefault
							}
						}
					}
				}

				tags(first: 1000) {
					edges {
						node {
							id
							text
						}
					}
				}

				badges(first: 1000) {
					edges {
						node {
							id
							...memberBadgeFragment
						}
					}
				}
			}
		}
		`,
		{ slug: club.slug },
	);

	const [commitMutation, isSubmitting] =
		useMutation<integracoesCheckoutProdutoNovoMutation>(
			graphql`
			mutation integracoesCheckoutProdutoNovoMutation(
				$club: String
				$integration: ID
				$input: PaymentIntegrationProductCreateInput!
			) {
				paymentIntegrationProductCreate(
					club: $club
					integration: $integration
					paymentIntegrationProductInput: $input
				) {
					id
					enabled
					productName
					productId
					accessGroup {
						accessGroup {
							courses {
								course {
									name
								}
							}
							spaces {
								name
								icon
							}
						}
					}
					integration {
						id
						products {
							id
							enabled
							productName
							productId
							accessGroup {
								accessGroup {
									courses {
										course {
											name
										}
									}
									spaces {
										name
										icon
									}
								}
							}
						}
					}
				}
			}
		`,
		);

	const integrations = data.club.integrations
		.filter(
			(
				it,
			): it is {
				readonly __typename: "PaymentIntegration";
				readonly id: string;
				readonly provider: keyof typeof PaymentProviderInfo;
			} =>
				it.__typename === "PaymentIntegration" &&
				it.provider !== "%future added value",
		)
		.map((it) => ({
			id: it.id,
			provider: it.provider,
			providerName: PaymentProviderInfo[it.provider]?.name || it.provider,
		}));

	const feeds = data.club.spaces.filter(Boolean).map((space) => ({
		id: space.id,
		name: space.name,
		icon: space.icon ?? null,
		groupName: space.group?.name,
	}));

	const courses = data.club.courses.edges.map(({ node: course }) => ({
		id: course.id,
		name: course.name,
		category: course.category,
		memberGroups: course.memberGroups.map((group) => ({
			id: group.id,
			name: group.name,
			isDefault: group.isDefault,
		})),
	}));

	const tags = data.club.tags.edges.map(({ node: tag }) => ({
		id: tag.id,
		text: tag.text,
	}));

	const badges = data.club.badges.edges.map(({ node: badge }) => badge);

	const handleSubmit = (values: OfferFormValues) => {
		const integrationId =
			values.type === "checkout" ? values.integrationId : null;

		commitMutation({
			variables: {
				club: club.slug,
				integration: integrationId,
				input: {
					productName: values.name,
					productId: values.productId || undefined,
					basePrice: values.basePrice || undefined,
					checkoutUrl: values.checkoutUrl || undefined,
					description: values.description,
					accessDefaultDuration: values.expirationDays || undefined,
					enabled: true,
					accessgroup: {
						spaces: values.feeds,
						courses: values.courses.map((c) => ({
							id: c.id,
							memberGroup: c.memberGroup || undefined,
						})),
						tags: values.tags.length > 0 ? values.tags : undefined,
						badges: values.badges.length > 0 ? values.badges : undefined,
					},
				},
			},
			onCompleted: (response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Oferta criada com sucesso");

				fetchQuery<integracoesCheckoutProdutoNovo_offersRefetchQuery>(
					environment,
					OffersRefetchQuery,
					{ slug: club.slug },
				).subscribe({});

				const createdIntegrationId =
					response.paymentIntegrationProductCreate.integration?.id;

				if (createdIntegrationId) {
					navigate({
						to: "/c/$club/configuracoes/integracoes/checkout/$id",
						params: { club: club.slug, id: createdIntegrationId },
					});
				} else {
					navigate({
						to: "/c/$club/monetizacao/ofertas",
						params: { club: club.slug },
					});
				}
			},
			onError: (error) => {
				toast.error("Erro ao criar oferta", {
					description: error.message,
				});
			},
		});
	};

	return (
		<div className="size-full flex items-start justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Nova Oferta</Heading>

				<OfferForm
					onSubmit={handleSubmit}
					integrations={integrations}
					feeds={feeds}
					courses={courses}
					tags={tags}
					badges={badges}
					lockedIntegrationId={lockedIntegrationId}
					isSubmitting={isSubmitting}
				/>
			</div>
		</div>
	);
}
